import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, limit, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { fetchSignInMethodsForEmail } from 'firebase/auth';
import { useAuth, USER_ROLES } from '../../context/AuthContext';
import { db, auth } from '../../firebase/config';
import otpAuthService from '../../services/otpAuthService';
import Layout from '../layout/Layout';
import './Auth.css';

const MentorInvite = () => {
  // Form fields
  const [email, setEmail] = useState('');
  
  // Form state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Invitation data
  const [invitations, setInvitations] = useState([]);
  const [remainingInvites, setRemainingInvites] = useState(3);
  
  const { currentUser, userRole } = useAuth();

  // Check if invitation is expired
  const isInvitationExpired = (invitation) => {
    if (!invitation.expiresAt) return false;
    const now = new Date();
    const expiresAt = invitation.expiresAt.toDate ? invitation.expiresAt.toDate() : new Date(invitation.expiresAt);
    return expiresAt < now;
  };
  
  // Load invitation history and check remaining invites
  useEffect(() => {
    if (!currentUser) return;
    
    const loadInvitations = async () => {
      try {
        // Get user's invitations (pending and expired)
        const invitationsRef = collection(db, 'invitations');
        const q = query(
          invitationsRef,
          where('inviterId', '==', currentUser.uid),
          where('status', 'in', ['pending', 'expired']),
          orderBy('createdAt', 'desc'),
          limit(20)
        );
        
        const querySnapshot = await getDocs(q);
        const invitationList = [];
        
        querySnapshot.forEach(doc => {
          invitationList.push({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate()
          });
        });
        
        setInvitations(invitationList);
        
        // Calculate remaining invites (if not admin) - only count pending invitations
        if (userRole !== USER_ROLES.ADMIN) {
          const pendingCount = invitationList.filter(inv => inv.status === 'pending' && !isInvitationExpired(inv)).length;
          setRemainingInvites(Math.max(0, 3 - pendingCount));
        } else {
          setRemainingInvites(Infinity);
        }
      } catch (error) {
        console.error('Failed to load invitations:', error);
        setError('招待履歴の読み込みに失敗しました。');
      }
    };
    
    loadInvitations();
  }, [currentUser, userRole]);
  
  // Generate random invite code (6 characters)
  const generateInviteCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Avoid confusing characters
    let code = '';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };
  
  // Validate form
  const validateForm = () => {
    setError('');
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('有効なメールアドレスを入力してください。');
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous messages
    setError('');
    setSuccessMessage('');
    
    // Validate form
    if (!validateForm()) return;
    
    // Check invitation limit
    if (userRole !== USER_ROLES.ADMIN && remainingInvites <= 0) {
      setError('招待数の上限（3件）に達しています。');
      return;
    }
    
    try {
      setLoading(true);
      
      // Check if email is already registered as a mentor
      const mentorsRef = collection(db, 'mentors');
      const mentorsQuery = query(mentorsRef, where('連絡先', '==', email));
      const mentorsSnapshot = await getDocs(mentorsQuery);
      
      if (!mentorsSnapshot.empty) {
        setError('このメールアドレスは既に登録されています。');
        setLoading(false);
        return;
      }

      // Check if email exists in Firebase Authentication
      try {
        const signInMethods = await fetchSignInMethodsForEmail(auth, email);
        if (signInMethods.length > 0) {
          setError('このメールアドレスは既に認証システムに登録されています。');
          setLoading(false);
          return;
        }
      } catch (authError) {
        // If the error is related to email enumeration protection, continue with the invitation
        console.log('Auth check error (may be due to email enumeration protection):', authError);
      }
      
      // Generate invite code
      const inviteCode = generateInviteCode();
      
      // Generate invitation URL
      const invitationUrl = `${window.location.origin}/mentor-register?email=${encodeURIComponent(email)}&code=${inviteCode}`;

      // Log generated URL for debugging
      console.log('Generated invitation URL:', invitationUrl);

      // Send mentor invitation email
      const otpResult = await otpAuthService.sendMentorInvitationEmail(email, inviteCode, invitationUrl);

      if (!otpResult.success) {
        throw new Error(otpResult.error || '招待メール送信に失敗しました');
      }
      
      // Save invitation to Firestore with mentor_pending role
      const invitationData = {
        email,
        inviteCode,
        status: 'pending',
        role: USER_ROLES.MENTOR_PENDING,  // Use constant from USER_ROLES
        createdAt: serverTimestamp(),
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days expiry
        inviterId: currentUser.uid,
        invitationUrl: invitationUrl
      };
      
      console.log('Saving invitation data:', invitationData);
      await addDoc(collection(db, 'invitations'), invitationData);
      
      // Update UI
      setSuccessMessage(`${email} に招待メールを送信しました。メールが届かない場合は、招待履歴からURLをコピーして共有することもできます。`);
      setEmail('');
      
      // Refresh invitations list
      const updatedInvitations = [...invitations, { ...invitationData, createdAt: new Date() }];
      setInvitations(updatedInvitations);
      
      // Update remaining invites
      if (userRole !== USER_ROLES.ADMIN) {
        setRemainingInvites(Math.max(0, remainingInvites - 1));
      }
      
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error('Mentor invitation error:', error);
      setError("OG招待の処理に失敗しました。時間をおいて再度お試しください。");
    }
  };
  

  // Format date for display
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return date.toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Generate invitation URL
  const generateInvitationUrl = (invitation) => {
    return `${window.location.origin}/mentor-register?email=${encodeURIComponent(invitation.email)}&code=${invitation.inviteCode}`;
  };

  // Copy invitation URL to clipboard
  const copyInvitationUrl = async (invitation) => {
    if (isInvitationExpired(invitation)) {
      setError('期限切れの招待URLはコピーできません。');
      return;
    }

    try {
      const invitationUrl = generateInvitationUrl(invitation);
      await navigator.clipboard.writeText(invitationUrl);
      setSuccessMessage(`${invitation.email} の招待URLをクリップボードにコピーしました！`);
      setError(''); // Clear any previous errors
    } catch (err) {
      console.error('Failed to copy invitation URL: ', err);
      setError('URLのコピーに失敗しました。手動でコピーしてください。');
    }
  };

  return (
    <Layout>
      <div className="login-box">
        <h2>OG招待</h2>
        
        {error && <div className="error-message">{error}</div>}
        {successMessage && <div className="success-message">{successMessage}</div>}
        
        {userRole !== USER_ROLES.ADMIN && (
          <div className={`invitation-limit-info ${remainingInvites === 0 ? 'limit-reached' : ''}`}>
            招待可能回数: 残り {remainingInvites} / 3 回
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">メールアドレス</label>
            <input 
              type="email" 
              id="email" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="OGのメールアドレス"
              disabled={loading || remainingInvites === 0}
              required
            />
          </div>
          
          <button 
            type="submit"
            className="auth-button"
            disabled={loading || remainingInvites === 0}
          >
            {loading ? '招待作成中...' : '招待メールを送信'}
          </button>
        </form>
        
        
        <div className="invitation-history">
          <h3>招待履歴</h3>
          {invitations.length === 0 ? (
            <p className="empty-list">招待履歴はありません</p>
          ) : (
            <div className="invitation-list">
              {invitations.map(invitation => {
                const expired = isInvitationExpired(invitation);
                return (
                  <div key={invitation.id || invitation.email} className="invitation-item">
                    <div className="invitation-details">
                      <div className="invitation-email">{invitation.email}</div>
                      <div className="invitation-meta">
                        招待コード: {invitation.inviteCode} | 
                        送信日時: {formatDate(invitation.createdAt)}
                        {invitation.expiresAt && (
                          <span className={`expiry-info ${expired ? 'expired' : ''}`}>
                            {' | '}有効期限: {formatDate(invitation.expiresAt.toDate ? invitation.expiresAt.toDate() : new Date(invitation.expiresAt))}
                            {expired && ' (期限切れ)'}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="invitation-actions">
                      <div className={`invitation-status ${expired ? 'status-expired' : 'status-pending'}`}>
                        {expired ? '期限切れ' : '未登録'}
                      </div>
                      <button 
                        type="button" 
                        onClick={() => copyInvitationUrl(invitation)}
                        className={`copy-url-button ${expired ? 'disabled' : ''}`}
                        disabled={expired}
                        title={expired ? '期限切れのため利用できません' : 'URLをコピー'}
                      >
                        {expired ? '期限切れ' : 'URLコピー'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default MentorInvite; 